// Наличността зад листа „Изписване": съставките на всяка неразделена рецепта
// (`/recipe-ingredients`, кеширани по рецепта — същите, по които „Раздели на
// продукти" прави редовете) и наличността + партидите КЪМ ДАТАТА за всеки
// продукт в документа — от продуктовите редове И от съставките на рецептите.
// От тях: нуждата по продукт за целия документ, кое не стига, колко порции
// стигат и стойността на всяка рецепта (writeoffStockCheck — чистата сметка).
//
// При РЕДАКЦИЯ наличността се пита БЕЗ собственото движение
// (`excludeMovementId`) — точно както я сверява BE при запис.
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { getRecipeIngredients, getStockAsOf } from "../../../../api/inventory";
import type { Product, RecipeIngredient, StockAsOf } from "../../../../api/inventory";
import { useIsEdgeLocal } from "../../../../hooks/useEdgeMode";
import {
  buildProductNeeds,
  findStockShortages,
  fittingPortions,
  recipeLineValues,
  shortagesOfLine,
  type IngredientsByRecipe,
  type RecipeLineValues,
  type StockShortage,
} from "./writeoffStockCheck";
import type { WriteoffLineDraft } from "./writeoffWorksheetShared";

// Съставките на рецепта се менят рядко (в org-а) — пет минути кеш
const RECIPE_INGREDIENTS_STALE_MS = 5 * 60 * 1000;

export const recipeIngredientsQuery = (recipeId: number) => ({
  queryKey: ["inventory", "recipe-ingredients", recipeId],
  queryFn: () => getRecipeIngredients(recipeId),
  staleTime: RECIPE_INGREDIENTS_STALE_MS,
});

// Какво знае листът за един ред-рецепта
export type RecipeRowStock = {
  values: RecipeLineValues;
  // съставките ѝ, които не стигат (нуждата е за целия документ)
  shortages: StockShortage[];
  // колко цели порции стигат при останалите редове (null — не се знае още)
  fittingPortions: number | null;
  // рецептата няма съставки (изписва нищо)
  empty: boolean;
  // колко продуктови реда дава „Раздели на продукти" (0 — още не се знае)
  ingredientCount: number;
};

type Params = {
  lines: WriteoffLineDraft[];
  warehouseId: number | null;
  asOfDayKey: string | null; // YYYY-MM-DD на избраната дата
  excludeMovementId: number | null; // редактираното изписване
  // автоматичен брак от Маркиране — роден с разрешен минус, не се проверява
  skipStockCheck: boolean;
  productById: Map<number, Product>;
  vatRegistered: boolean;
};

export function useWriteoffStock({
  lines,
  warehouseId,
  asOfDayKey,
  excludeMovementId,
  skipStockCheck,
  productById,
  vatRegistered,
}: Params) {
  const isLocal = useIsEdgeLocal();

  // ─── Съставките на неразделените рецепти ─────────────────────────────────
  const recipeIds = useMemo(
    () => [
      ...new Set(
        lines.flatMap((line) => (line.kind === "recipe" && line.recipeId != null ? [line.recipeId] : [])),
      ),
    ],
    [lines],
  );
  const ingredientQueries = useQueries({
    queries: recipeIds.map((recipeId) => ({ ...recipeIngredientsQuery(recipeId), enabled: !isLocal })),
  });
  const ingredientsByRecipe: IngredientsByRecipe = new Map();
  recipeIds.forEach((recipeId, index) => {
    const data = ingredientQueries[index]?.data;
    if (data) ingredientsByRecipe.set(recipeId, data);
  });
  // Ключът на заредените данни — мемоизацията по-долу да не зависи от масиви,
  // които са нови на всеки рендер
  const ingredientsKey = ingredientQueries.map((query) => query.dataUpdatedAt).join(",");

  // ─── Наличност/партиди КЪМ ДАТАТА (продукти + съставки) ──────────────────
  const productIds = useMemo(() => {
    const ids = new Set<number>();
    for (const line of lines) if (line.kind === "product" && line.productId != null) ids.add(line.productId);
    for (const ingredients of ingredientsByRecipe.values())
      for (const ingredient of ingredients) ids.add(ingredient.productId);
    return [...ids];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, ingredientsKey]);
  const asOfQueries = useQueries({
    queries: productIds.map((productId) => ({
      queryKey: ["inventory", "stock-as-of", warehouseId, productId, asOfDayKey, excludeMovementId],
      queryFn: () =>
        getStockAsOf(warehouseId as number, productId, asOfDayKey as string, excludeMovementId),
      enabled: !!warehouseId && !!asOfDayKey,
    })),
  });
  const asOfByProduct = new Map<number, StockAsOf>();
  productIds.forEach((productId, index) => {
    const data = asOfQueries[index]?.data;
    if (data) asOfByProduct.set(productId, data);
  });
  const asOfKey = asOfQueries.map((query) => query.dataUpdatedAt).join(",");

  // ─── Нуждата, недостигът и рецептите ─────────────────────────────────────
  const stock = useMemo(() => {
    const needs = buildProductNeeds(lines, ingredientsByRecipe);
    const shortages = skipStockCheck ? [] : findStockShortages(needs, asOfByProduct, productById);
    const overProductIds = new Set(shortages.map((shortage) => shortage.productId));
    const recipeStockByKey = new Map<number, RecipeRowStock>();
    for (const line of lines) {
      if (line.kind !== "recipe" || line.recipeId == null) continue;
      const ingredients: RecipeIngredient[] | undefined = ingredientsByRecipe.get(line.recipeId);
      recipeStockByKey.set(line.key, {
        values: recipeLineValues(line, ingredients, asOfByProduct, productById, vatRegistered),
        shortages: shortagesOfLine(line.key, shortages),
        fittingPortions: fittingPortions(line, ingredients, needs, asOfByProduct),
        empty: ingredients != null && !ingredients.some((ingredient) => ingredient.quantity > 0),
        ingredientCount: ingredients?.length ?? 0,
      });
    }
    return { needs, shortages, overProductIds, recipeStockByKey };
    // asOfByProduct/ingredientsByRecipe се пресъздават всеки рендер; зависим
    // от данните зад тях (ключовете им) и от това КОЯ наличност е питана
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lines,
    ingredientsKey,
    asOfKey,
    warehouseId,
    asOfDayKey,
    excludeMovementId,
    skipStockCheck,
    productById,
    vatRegistered,
  ]);

  return { asOfByProduct, ingredientsByRecipe, ...stock };
}
